// Text measurement for PDF layout decisions (2D-table column widths).
//
// The browser measures with a canvas in the platform's system font. The headless
// renderer has no canvas, so it installs a font-metrics measurer with
// `setPdfTextMeasurer`. Both return CSS pixels for `font` in the shorthand form
// "<weight> <size>px" (the family is the caller's concern).

export type PdfTextMeasurer = (text: string, font: string) => number;

const SYSTEM_FONT =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif';

let installed: PdfTextMeasurer | null = null;
let _measureCanvas: HTMLCanvasElement | null = null;

export function setPdfTextMeasurer(measurer: PdfTextMeasurer | null): void {
  installed = measurer;
}

function fallbackPx(text: string, font: string): number {
  const size = parseFloat(font.match(/(\d+(?:\.\d+)?)px/)?.[1] ?? "14");
  return text.length * size * 0.58;
}

export function measureTextPx(text: string, font: string): number {
  if (installed) return installed(text, font);
  if (typeof document === "undefined") return fallbackPx(text, font);
  _measureCanvas = _measureCanvas ?? document.createElement("canvas");
  const ctx = _measureCanvas.getContext("2d");
  if (!ctx) return fallbackPx(text, font);
  ctx.font = `${font} ${SYSTEM_FONT}`;
  return ctx.measureText(text).width;
}
